/**
 * Performance Monitor
 * Client-side Core Web Vitals and performance monitoring
 * 
 * Features:
 * - LCP (Largest Contentful Paint) tracking
 * - INP (Interaction to Next Paint) tracking
 * - CLS (Cumulative Layout Shift) tracking
 * - FCP (First Contentful Paint) tracking
 * - Performance metrics reporting
 * 
 * Owner: Theme/Dev
 */

(function() {
  'use strict';

  function debugLog() {
    if (window.location.hostname === 'localhost' || window.location.hostname.includes('myshopify.com')) {
      if (window.logger && typeof window.logger.debug === 'function') {
        window.logger.debug.apply(window.logger, arguments);
      }
    }
  }

  const TARGETS = {
    LCP: 2500,
    INP: 200,
    CLS: 0.1,
    FCP: 1000,
    TTFB: 800
  };

  const metrics = {
    LCP: null,
    INP: null,
    CLS: 0,
    FCP: null,
    TTFB: null
  };

  /**
   * Track Largest Contentful Paint
   */
  function trackLCP() {
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          metrics.LCP = Math.round(lastEntry.renderTime || lastEntry.loadTime);
          
          // Report to analytics
          reportMetric('LCP', metrics.LCP, TARGETS.LCP);
        });
        
        observer.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        // LCP not supported
      }
    }
  }

  /**
   * Track Interaction to Next Paint
   */
  function trackINP() {
    if ('PerformanceObserver' in window) {
      try {
        let interactionCount = 0;
        let maxDelay = 0;
        
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach(entry => {
            if (entry.duration) {
              interactionCount++;
              if (entry.duration > maxDelay) {
                maxDelay = entry.duration;
              }
            }
          });
          
          if (interactionCount > 0) {
            metrics.INP = Math.round(maxDelay);
            
            // Report after 5 interactions
            if (interactionCount >= 5) {
              reportMetric('INP', metrics.INP, TARGETS.INP);
            }
          }
        });
        
        observer.observe({ entryTypes: ['event'] });
      } catch (e) {
        // INP tracking fallback
        trackINPFallback();
      }
    } else {
      trackINPFallback();
    }
  }

  /**
   * Fallback INP tracking using click events
   */
  function trackINPFallback() {
    let maxDelay = 0;
    let clickCount = 0;
    
    document.addEventListener('click', function(e) {
      const clickTime = performance.now();
      
      requestAnimationFrame(() => {
        const delay = performance.now() - clickTime;
        clickCount++;
        if (delay > maxDelay) {
          maxDelay = delay;
        }
        
        if (clickCount >= 5) {
          metrics.INP = Math.round(maxDelay);
          reportMetric('INP', metrics.INP, TARGETS.INP);
        }
      });
    }, { passive: true });
  }

  /**
   * Track Cumulative Layout Shift
   */
  function trackCLS() {
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach(entry => {
            if (!entry.hadRecentInput && entry.value) {
              metrics.CLS += entry.value;
            }
          });
          
          // Report CLS
          reportMetric('CLS', metrics.CLS, TARGETS.CLS);
        });
        
        observer.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        // CLS not supported
      }
    }
  }

  /**
   * Track First Contentful Paint
   */
  function trackFCP() {
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach(entry => {
            if (entry.name === 'first-contentful-paint') {
              metrics.FCP = Math.round(entry.startTime);
              reportMetric('FCP', metrics.FCP, TARGETS.FCP);
            }
          });
        });
        
        observer.observe({ entryTypes: ['paint'] });
      } catch (e) {
        // FCP not supported
      }
    }
  }

  /**
   * Track Time to First Byte
   */
  function trackTTFB() {
    if (performance.timing) {
      const timing = performance.timing;
      const ttfb = timing.responseStart - timing.requestStart;
      metrics.TTFB = Math.round(ttfb);
      reportMetric('TTFB', metrics.TTFB, TARGETS.TTFB);
    } else if (performance.getEntriesByType) {
      const navigationEntry = performance.getEntriesByType('navigation')[0];
      if (navigationEntry) {
        const ttfb = navigationEntry.responseStart - navigationEntry.requestStart;
        metrics.TTFB = Math.round(ttfb);
        reportMetric('TTFB', metrics.TTFB, TARGETS.TTFB);
      }
    }
  }

  /**
   * Report metric to analytics
   */
  function reportMetric(name, value, target) {
    const passed = value <= target;
    
    // Report to dataLayer if available
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'web_vitals',
        metric_name: name,
        metric_value: value,
        metric_target: target,
        metric_passed: passed
      });
    }
    
    // Report to gtag if available
    if (typeof gtag !== 'undefined') {
      gtag('event', 'web_vital', {
        event_category: 'Web Vitals',
        event_label: name,
        value: value,
        non_interaction: true
      });
    }
    
    // Console log in development
    const status = passed ? 'OK' : 'WARN';
    debugLog(`[Performance] ${status} ${name}: ${value} (target: ${target})`);
  }

  /**
   * Initialize all tracking
   */
  function init() {
    // Wait for page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        trackFCP();
        trackTTFB();
      });
    } else {
      trackFCP();
      trackTTFB();
    }
    
    // Track LCP and CLS immediately
    trackLCP();
    trackCLS();
    
    // Track INP after page is interactive
    if (document.readyState === 'complete') {
      trackINP();
    } else {
      window.addEventListener('load', trackINP);
    }
    
    // Report all metrics on page unload
    window.addEventListener('beforeunload', function() {
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'performance_summary',
          metrics: metrics
        });
      }
    });
  }

  // Initialize
  init();

  // Export for external use
  window.KTPerformanceMonitor = {
    getMetrics: () => ({ ...metrics }),
    reportMetric: reportMetric
  };
})();
